
export default {
  name: 'PricingIndex',
  layout: 'default-no-bg',
  nuxtI18n: {
    paths: {
      de: '/preise',
      en: '/pricing', // -> accessible at /about-us (no prefix since it's the default locale)
    },
  },
  async asyncData({ app, $prismic, params, error }) {
    const pricingData = await $prismic.api.getSingle('pricing', {
      lang: app.i18n.localeProperties.prismic,
    })

    const comparisonMatrix = pricingData.data?.category_order?.map(
      (category) => {
        const categoryFeatures =
          pricingData.data?.comparison_table?.filter((feature) => {
            return feature.category === category.category_name
          }) || []

        return { [category.category_name]: categoryFeatures }
      },
    )

    const result = comparisonMatrix.reduce((accu, current) => {
      return { ...accu, ...current }
    }, {})

    return {
      pricingData: pricingData.data,
      comparisonMatrix: result,
    }
  },
  data() {
    return {
      customer: null,
      loadingCheckout: null,
      loadingPortal: null,
      sendingCancellationTicket: null,
      sendingCancellationTicketDone: null,
    }
  },
  head() {
    const i18nHEAD = this.$nuxtI18nHead({ addSeoAttributes: true })

    i18nHEAD.link = i18nHEAD.link.map((link) => {
      if (!link.href.endsWith('/')) {
        link.href = link.href + '/'
      }
      return link
    })

    return {
      title: this.pricingData.meta_title + ' | ATHLYZER',
      htmlAttrs: {
        ...i18nHEAD.htmlAttrs,
      },
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: this.pricingData.meta_description,
        },
        {
          hid: 'og-title',
          property: 'og:title',
          content: this.pricingData.meta_title + ' | ATHLYZER',
        },
        {
          hid: 'og-desc',
          property: 'og:description',
          content: this.pricingData.meta_description,
        },
        {
          hid: 'og-image',
          property: 'og:image',
          content:
            this.pricingData.meta_open_graph_image.url || '/ATHLYZER_OG.jpg',
        },
        ...i18nHEAD.meta,
      ],
      link: [...i18nHEAD.link],
    }
  },
  computed: {
    getPrices() {
      return this.$store.state.pricing.prices[
        this.$store.state.pricing.interval
      ][this.$store.state.pricing.paymentInterval]
    },
    getNewPrices() {
      return this.$store.state.pricing.newPrices[
        this.$store.state.pricing.interval
      ][this.$store.state.pricing.paymentInterval]
    },
    licenseLevel() {
      return this.$store.getters['pricing/getLicenseLevel']
    },
    calculateRookieDiscount() {
      const maxPrice = this.$store.state.pricing.prices['1-year'].monthly.rookie
      const currentPrice =
        this.$store.state.pricing.prices[this.$store.state.pricing.interval][
          this.$store.state.pricing.paymentInterval
        ].rookie

      const discount = (1 - currentPrice / maxPrice) * 100
      const discountString = discount.toLocaleString(undefined, {
        maximumFractionDigits: 0,
      })

      return discountString
    },
    calculateProfessionalDiscount() {
      const maxPrice =
        this.$store.state.pricing.prices['1-year'].monthly.professional
      const currentPrice =
        this.$store.state.pricing.prices[this.$store.state.pricing.interval][
          this.$store.state.pricing.paymentInterval
        ].professional

      const discount = (1 - currentPrice / maxPrice) * 100
      const discountString = discount.toLocaleString(undefined, {
        maximumFractionDigits: 0,
      })

      return discountString
    },
  },
  methods: {
    formatCurrency(value) {
      // Check if the value has a fractional part
      if (value % 1 !== 0) {
        // If yes, format with two decimal places
        return this.$n(value, {
          style: 'currency',
          currency: 'EUR', // Change the currency as needed
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })
      } else {
        // If no, format without decimal places
        return this.$n(value, {
          style: 'currency',
          currency: 'EUR', // Change the currency as needed
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })
      }
    },
    changeDuration(newDuration) {
      this.$store.commit('pricing/setInterval', newDuration)
    },
    changePaymentInterval(newInterval) {
      this.$store.commit('pricing/setPaymentInterval', newInterval)
    },
    async createCheckout(price) {
      const requestBody = {
        product: price,
        payment_interval: this.$store.state.pricing.paymentInterval,
        contract_duration: this.$store.state.pricing.interval,
        quantity: 1,
      }

      requestBody.success_url =
        'https://www.athlyzer.com' +
        this.localePath({ name: 'pricing-success' }) +
        '?utm_medium=customer-portal&utm_source=stripe&utm_campaign=success&utm_content=' +
        price +
        '-' +
        this.$store.state.pricing.interval +
        '-' +
        this.$store.state.pricing.paymentInterval +
        '&' +
        'paket=' +
        price +
        '-' +
        this.$store.state.pricing.interval +
        '-' +
        this.$store.state.pricing.paymentInterval +
        '&' +
        'value=' +
        this.$store.state.pricing.prices[this.$store.state.pricing.interval][
          this.$store.state.pricing.paymentInterval
        ][price]

      requestBody.cancel_url =
        'https://www.athlyzer.com' +
        this.$route.path +
        '?utm_medium=customer-portal&utm_source=stripe&utm_campaign=cancelled&utm_content=' +
        price +
        '-' +
        this.$store.state.pricing.interval +
        '-' +
        this.$store.state.pricing.paymentInterval

      if (this.$store.state.auth.customer?.id) {
        requestBody.customer_id = this.$store.state.auth.customer.id

        try {
          this.loadingCheckout = price

          this.$gtag.event('begin_checkout', {
            event_category: 'engagement',
            event_label: price,
          })

          this.$mixpanel.track('website.checkout.begin', {
            price,
          })

          const session = await this.$superlogin
            .getHttp()
            .post('/user/stripe/checkout', requestBody)

          this.loadingCheckout = null

          if (
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent,
            )
          ) {
            window.location.href = session.data.url
          } else {
            window.open(session.data.url)
          }
        } catch (e) {
          this.loadingCheckout = null
          this.$sentry.captureException(e)
        }
      } else {
        this.$router.push({
          path: '/login',
          query: {
            redirect: this.$route.path,
          },
        })
      }
    },
    async openCustomerDashboard() {
      try {
        const requestBody = {}

        if (this.$store.state.auth.customer?.id) {
          requestBody.customer_id = this.$store.state.auth.customer.id

          this.loadingPortal = true
          const session = await this.$superlogin
            .getHttp()
            .post('/user/stripe/billing-portal', requestBody)
          this.loadingPortal = null

          if (
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
              navigator.userAgent,
            )
          ) {
            window.location.href = session.data.url
          } else {
            window.open(session.data.url)
          }
        }
      } catch (error) {
        this.loadingPortal = null
        this.$sentry.captureException(error)
      }
    },
    async sendCancellationTicket() {
      try {
        this.sendingCancellationTicket = true
        const requestBody = {
          description:
            'Bitte kündigt meinen Vertrag zum nächstmöglichen Zeitpunkt: ' +
            this.$store.state.pricing.paketName,
          email: this.$superlogin.getSession()?.email,
        }

        await this.$superlogin
          .getHttp()
          .post('/user/support/cancel', requestBody)

        this.sendingCancellationTicketDone = true
        this.sendingCancellationTicket = null
      } catch (error) {
        this.sendingCancellationTicket = null
        this.$sentry.captureException(error)
      }
    },
    toggleSentConfirmationModal() {
      this.sendingCancellationTicketDone = !this.sendingCancellationTicketDone
    },
    trackDemoBookingEventAndConversion(eventLabel) {
      this.$gtag.event('demo_booking', {
        event_label: eventLabel,
      })

      this.$gtag.event('conversion', {
        send_to: 'AW-10985098189/ZFfDCKKYspQYEM2XjfYo',
      })
    },
  },
}
